/**
 * Utilidad única para cálculo de totales de materiales/líneas.
 *
 * Reglas (alineadas con backend `routes/ordenes_routes.py`):
 * - IVA por línea: respeta `material.iva` aunque sea 0 (exento). Solo cae al
 *   default si el valor es null/undefined/NaN.
 * - Descuento por línea: aplicado sobre el subtotal antes del IVA.
 * - Etiqueta de IVA: si todos los materiales usan el mismo %, devuelve ese %;
 *   si hay mezcla, devuelve `null` (renderizar como "varios tipos").
 *
 * Centralizar esta lógica evita divergencias entre vistas (detalle, PDF,
 * factura, presupuesto…) que en el pasado provocaron bugs de IVA al 21%
 * forzado en lugar del exento real.
 */

/**
 * Parsea un valor de IVA (puede venir como string o number) respetando 0.
 * @param {*} v
 * @param {number} fallback - usado solo si v es null/undefined/NaN
 */
export function parseIva(v, fallback = 21) {
  if (v === null || v === undefined || v === '') return fallback;
  const n = Number(v);
  return Number.isNaN(n) ? fallback : n;
}

/**
 * Calcula los totales agregados de una lista de materiales.
 *
 * @param {Array} materiales  Lista de objetos { cantidad, precio_unitario, iva?, descuento? }
 * @param {object} [opts]
 * @param {number} [opts.ivaDefault=21]    IVA a usar si `material.iva` no está definido.
 * @param {number} [opts.transporte=0]     Sumado al total final.
 * @returns {{ base:number, iva:number, total:number, ivaPct:number|null }}
 *   - base: suma de subtotales con descuento por línea, sin IVA
 *   - iva: suma de IVAs por línea
 *   - total: base + iva + transporte
 *   - ivaPct: % común si todos coinciden, null si hay mezcla
 */
export function calcularTotales(materiales, opts = {}) {
  const { ivaDefault = 21, transporte = 0 } = opts;
  let base = 0;
  let iva = 0;
  const ivasUsados = new Set();

  (materiales || []).forEach((m) => {
    const cantidad = Number(m.cantidad) || 0;
    const precio = Number(m.precio_unitario) || 0;
    const descPct = Number(m.descuento) || 0;
    const st = cantidad * precio;
    const subtotal = st - (descPct ? st * descPct / 100 : 0);
    const pct = parseIva(m.iva, ivaDefault);
    base += subtotal;
    iva += subtotal * (pct / 100);
    ivasUsados.add(pct);
  });

  const ivaPct = ivasUsados.size === 1 ? [...ivasUsados][0] : null;
  return {
    base,
    iva,
    total: base + iva + (Number(transporte) || 0),
    ivaPct,
  };
}

/**
 * Calcula el subtotal con IVA de una sola línea (utilidad menor).
 */
export function calcularLinea(material, ivaDefault = 21) {
  const cantidad = Number(material.cantidad) || 0;
  const precio = Number(material.precio_unitario) || 0;
  const descPct = Number(material.descuento) || 0;
  const st = cantidad * precio;
  const baseLinea = st - (descPct ? st * descPct / 100 : 0);
  const pct = parseIva(material.iva, ivaDefault);
  const ivaLinea = baseLinea * (pct / 100);
  return { base: baseLinea, iva: ivaLinea, total: baseLinea + ivaLinea, ivaPct: pct };
}
